import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Alert,
  LinearProgress,
  Avatar,
  Badge,
  Switch,
  FormControlLabel,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  AdminPanelSettings as AdminIcon,
  AccountCircle as UserIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Security as SecurityIcon,
  SupervisorAccount as SupervisorIcon,
  Palette as PaletteIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { authService } from '../services/authService';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { el } from 'date-fns/locale';

// ✅ NEW: Available profile colors for meeting assignments
const PROFILE_COLORS = [
  { value: '#0015ff', name: 'Μπλε', color: '#0015ff' },
  { value: '#ff00a1', name: 'Ροζ', color: '#ff00a1' },
  { value: '#2e7d32', name: 'Πράσινο', color: '#2e7d32' },
  { value: '#8400ff', name: 'Μοβ', color: '#8400ff' },
  { value: '#0dff9a', name: 'Τιρκουάζ', color: '#0dff9a' },
  { value: '#000000', name: 'Μαύρο', color: '#000000' }
];

// API functions using authenticated service
const userApi = {
  getAll: async () => {
    const axios = (await import('axios')).default;
    const token = localStorage.getItem('token');
    return axios.get('/api/users', {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => res.data);
  },
  create: async (userData) => {
    const axios = (await import('axios')).default;
    const token = localStorage.getItem('token');
    return axios.post('/api/users', userData, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => res.data);
  },
  update: async (id, userData) => {
    const axios = (await import('axios')).default;
    const token = localStorage.getItem('token');
    return axios.put(`/api/users/${id}`, userData, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => res.data);
  },
  delete: async (id) => {
    const axios = (await import('axios')).default;
    const token = localStorage.getItem('token');
    return axios.delete(`/api/users/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => res.data);
  },
  changePassword: async (id, passwordData) => {
    const axios = (await import('axios')).default;
    const token = localStorage.getItem('token');
    return axios.put(`/api/users/${id}/password`, passwordData, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => res.data);
  }
};

const UserManagement = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogType, setDialogType] = useState('create'); // 'create', 'edit', 'delete', 'password'
  const [formData, setFormData] = useState({
    username: '',
    firstname: '',
    lastname: '',
    email: '',
    password: '',
    role: 'user',
    is_active: true,
    profile_color: '#0015ff' // ✅ NEW: Default profile color
  });
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);

  // Fetch users
  const { data: usersResponse, isLoading, error, refetch } = useQuery('users', userApi.getAll);

  // Mutations
  const createMutation = useMutation(userApi.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('users');
      toast.success('✅ Χρήστης δημιουργήθηκε επιτυχώς');
      handleCloseDialog();
    },
    onError: (error) => {
      toast.error(`❌ Σφάλμα: ${error.response?.data?.message || error.message}`);
    }
  });

  const updateMutation = useMutation(({ id, data }) => userApi.update(id, data), {
    onSuccess: () => {
      queryClient.invalidateQueries('users');
      toast.success('✅ Χρήστης ενημερώθηκε επιτυχώς');
      handleCloseDialog();
    },
    onError: (error) => {
      toast.error(`❌ Σφάλμα: ${error.response?.data?.message || error.message}`);
    }
  });

  const deleteMutation = useMutation(userApi.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('users');
      toast.success('✅ Χρήστης διαγράφηκε επιτυχώς');
      handleCloseDialog();
    },
    onError: (error) => {
      toast.error(`❌ Σφάλμα: ${error.response?.data?.message || error.message}`);
    }
  });

  const passwordMutation = useMutation(({ id, data }) => userApi.changePassword(id, data), {
    onSuccess: () => {
      toast.success('✅ Κωδικός πρόσβασης άλλαξε επιτυχώς');
      handleCloseDialog();
    },
    onError: (error) => {
      toast.error(`❌ Σφάλμα: ${error.response?.data?.message || error.message}`);
    }
  });

  // Filter users based on search
  const users = usersResponse?.data || [];
  const filteredUsers = users.filter(user =>
    user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.firstname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.lastname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOpenDialog = (type, user = null) => {
    setDialogType(type);
    setSelectedUser(user);
    
    if (type === 'create') {
      setFormData({
        username: '',
        firstname: '',
        lastname: '',
        email: '',
        password: '',
        role: 'user',
        is_active: true
      });
    } else if (type === 'edit' && user) {
      setFormData({
        username: user.username || '',
        firstname: user.firstname || '',
        lastname: user.lastname || '',
        email: user.email || '',
        password: '',
        role: user.role || 'user',
        is_active: user.is_active !== undefined ? user.is_active : true,
        profile_color: user.profile_color || '#0015ff' // ✅ NEW: Load profile color
      });
    } else if (type === 'password') {
      setPasswordData({
        newPassword: '',
        confirmPassword: ''
      });
    }
    
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setSelectedUser(null);
    setFormData({
      username: '',
      firstname: '',
      lastname: '',
      email: '',
      password: '',
      role: 'user',
      is_active: true
    });
    setPasswordData({
      newPassword: '',
      confirmPassword: ''
    });
    setShowPassword(false);
  };

  const handleSave = () => {
    if (dialogType === 'create') {
      if (!formData.username || !formData.email || !formData.password) {
        toast.error('❌ Παρακαλώ συμπληρώστε όλα τα υποχρεωτικά πεδία');
        return;
      }
      createMutation.mutate(formData);
    } else if (dialogType === 'edit') {
      const updateData = { ...formData };
      delete updateData.password; // Don't send empty password
      updateMutation.mutate({ id: selectedUser.id, data: updateData });
    } else if (dialogType === 'delete') {
      deleteMutation.mutate(selectedUser.id);
    } else if (dialogType === 'password') {
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        toast.error('❌ Οι κωδικοί πρόσβασης δεν ταιριάζουν');
        return;
      }
      if (passwordData.newPassword.length < 6) {
        toast.error('❌ Ο κωδικός πρόσβασης πρέπει να έχει τουλάχιστον 6 χαρακτήρες');
        return;
      }
      passwordMutation.mutate({ 
        id: selectedUser.id, 
        data: { newPassword: passwordData.newPassword } 
      });
    }
  };

  const getRoleChip = (role) => {
    const isAdmin = role === 'admin';
    return (
      <Chip
        icon={isAdmin ? <AdminIcon /> : <UserIcon />}
        label={isAdmin ? 'Διαχειριστής' : 'Χρήστης'}
        color={isAdmin ? 'primary' : 'default'}
        size="small"
      />
    );
  };

  const getStatusChip = (isActive) => {
    return (
      <Chip
        label={isActive ? 'Ενεργός' : 'Ανενεργός'}
        color={isActive ? 'success' : 'error'}
        size="small"
      />
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: el });
  };

  const getDialogTitle = () => {
    switch (dialogType) {
      case 'create': return 'Δημιουργία Νέου Χρήστη';
      case 'edit': return 'Επεξεργασία Χρήστη';
      case 'delete': return 'Διαγραφή Χρήστη';
      case 'password': return 'Αλλαγή Κωδικού Πρόσβασης';
      default: return '';
    }
  };

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          ❌ Σφάλμα φόρτωσης χρηστών: {error.response?.data?.message || error.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', fontSize: '1.5rem', color: 'rgba(0, 0, 0, 0.87)' }}>
          Διαχείριση Χρηστών
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Διαχειρίζεσαι τους χρήστες του συστήματος παρακολούθησης συνεδριών
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Σύνολο Χρηστών
                  </Typography>
                  <Typography variant="h4">
                    {users.length}
                  </Typography>
                </Box>
                <PersonIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Διαχειριστές
                  </Typography>
                  <Typography variant="h4">
                    {users.filter(u => u.role === 'admin').length}
                  </Typography>
                </Box>
                <AdminIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Ενεργοί Χρήστες
                  </Typography>
                  <Typography variant="h4">
                    {users.filter(u => u.is_active).length}
                  </Typography>
                </Box>
                <Badge color="success" variant="dot">
                  <SupervisorIcon color="success" sx={{ fontSize: 40 }} />
                </Badge>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Απλοί Χρήστες
                  </Typography>
                  <Typography variant="h4">
                    {users.filter(u => u.role === 'user').length}
                  </Typography>
                </Box>
                <UserIcon color="action" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Controls */}
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <TextField
          placeholder="Αναζήτηση χρηστών..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 300 }}
        />
        
        <Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            sx={{ mr: 1 }}
          >
            Ανανέωση
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('create')}
          >
            Νέος Χρήστης
          </Button>
        </Box>
      </Box>

      {/* Users Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {isLoading && <LinearProgress />}
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Χρήστης</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Ρόλος</TableCell>
                  <TableCell>Κατάσταση</TableCell>
                  <TableCell>Τελευταία Σύνδεση</TableCell>
                  <TableCell align="right">Ενέργειες</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="textSecondary" py={4}>
                        {searchTerm ? '🔍 Δεν βρέθηκαν χρήστες' : '👥 Δεν υπάρχουν χρήστες'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Avatar sx={{ mr: 2, bgcolor: user.role === 'admin' ? 'primary.main' : 'grey.500' }}>
                            {user.role === 'admin' ? <AdminIcon /> : <UserIcon />}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                              {user.username}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {user.firstname && user.lastname ? `${user.firstname} ${user.lastname}` : 'Δεν έχει οριστεί όνομα'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{getRoleChip(user.role)}</TableCell>
                      <TableCell>{getStatusChip(user.is_active)}</TableCell>
                      <TableCell>{formatDate(user.last_login)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Επεξεργασία">
                          <IconButton 
                            size="small"
                            onClick={() => handleOpenDialog('edit', user)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Αλλαγή Κωδικού">
                          <IconButton 
                            size="small"
                            onClick={() => handleOpenDialog('password', user)}
                          >
                            <SecurityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Διαγραφή">
                          <IconButton 
                            size="small"
                            color="error"
                            onClick={() => handleOpenDialog('delete', user)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={showDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {getDialogTitle()}
        </DialogTitle>
        <DialogContent dividers>
          {dialogType === 'delete' ? (
            <Alert severity="warning">
              <Typography>
                Είστε σίγουροι ότι θέλετε να διαγράψετε τον χρήστη <strong>{selectedUser?.username}</strong>;
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.
              </Typography>
            </Alert>
          ) : dialogType === 'password' ? (
            <Box>
              <TextField
                fullWidth
                label="Νέος Κωδικός Πρόσβασης"
                type={showPassword ? 'text' : 'password'}
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Επιβεβαίωση Κωδικού"
                type={showPassword ? 'text' : 'password'}
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                margin="normal"
                error={passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword}
                helperText={passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword ? 'Οι κωδικοί δεν ταιριάζουν' : ''}
              />
            </Box>
          ) : (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Όνομα Χρήστη *"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    disabled={dialogType === 'edit'}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Όνομα"
                    value={formData.firstname}
                    onChange={(e) => setFormData({ ...formData, firstname: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Επώνυμο"
                    value={formData.lastname}
                    onChange={(e) => setFormData({ ...formData, lastname: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email *"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </Grid>
                {dialogType === 'create' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Κωδικός Πρόσβασης *"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)}>
                              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Ρόλος</InputLabel>
                    <Select
                      value={formData.role}
                      label="Ρόλος"
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    >
                      <MenuItem value="user">
                        <Box display="flex" alignItems="center">
                          <UserIcon sx={{ mr: 1 }} />
                          Χρήστης (Μόνο παρακολούθηση)
                        </Box>
                      </MenuItem>
                      <MenuItem value="admin">
                        <Box display="flex" alignItems="center">
                          <AdminIcon sx={{ mr: 1 }} />
                          Διαχειριστής (Πλήρη πρόσβαση)
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* ✅ NEW: Profile Color Selection */}
                <Grid item xs={12}>
                  <Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        mb: 2, 
                        display: 'flex', 
                        alignItems: 'center',
                        color: 'text.secondary'
                      }}
                    >
                      <PaletteIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                      Χρώμα Προφίλ (για εμφάνιση στα meetings)
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {PROFILE_COLORS.map((colorOption) => (
                        <Box
                          key={colorOption.value}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            cursor: 'pointer',
                            p: 1,
                            borderRadius: 1,
                            border: formData.profile_color === colorOption.value ? '2px solid #2088ff' : '2px solid transparent',
                            backgroundColor: formData.profile_color === colorOption.value ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                          }}
                          onClick={() => setFormData({ ...formData, profile_color: colorOption.value })}
                        >
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              backgroundColor: colorOption.color,
                              borderRadius: '50%',
                              border: '2px solid #fff',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                              mb: 0.5
                            }}
                          />
                          <Typography variant="caption" sx={{ fontSize: '0.65rem', textAlign: 'center' }}>
                            {colorOption.name}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      />
                    }
                    label="Ενεργός λογαριασμός"
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<CancelIcon />}>
            Ακύρωση
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSave}
            startIcon={<SaveIcon />}
            color={dialogType === 'delete' ? 'error' : 'primary'}
            disabled={createMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading || passwordMutation.isLoading}
          >
            {dialogType === 'delete' ? 'Διαγραφή' : 'Αποθήκευση'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
